import Box from "@mui/material/Box";
import { styled, Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import {
  LowerTopToolbarContainer,
  TableTitle,
  TableTopToolbarContainer,
  TeamsTabs,
  ToolbarButtons,
  ToolbarButtonsContainer,
} from "_shared/teams/teams-page";
import { PageTab } from "_shared/teams/teams-tabs";
import { uniqBy } from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLoaderData } from "react-router";
import { useSession } from "users/__session/session-context";
import {
  getErrorLoadingTeamsByQuery,
  getIsLoadingTeamsByQuery,
  getTeamsByQuery,
  getTeamsQueryByInterface,
  populateTeamsByQuery,
  updateTeamsInterfaceQuery,
} from "work/work-agreements/teams/vendor-team/_store/vendor-teams-redux-slice";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { MRTNoMemo } from "_shared/_views/tables/table";

const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const columnHelper = createMRTColumnHelper<VendorTeam>();

export default function ActiveTeams() {
  // State
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState();

  // Route
  const routeParams = useLoaderData();
  const activePageTab = routeParams.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Templates;

  // Hooks
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();

  // Queries
  const interfaceName = "ActiveTeamsPage";
  const workingForMe = activePageTab === PageTab.TeamsWorkingForMe;
  const vendorTeamsQuery = useSelector(getTeamsQueryByInterface(interfaceName));
  const vendorTeamsResponse = useSelector(getTeamsByQuery(interfaceName, workingForMe));
  const isLoadingVendorTeams = useSelector(getIsLoadingTeamsByQuery(interfaceName, workingForMe));
  const errorLoadingVendorTeams = useSelector(getErrorLoadingTeamsByQuery(interfaceName, workingForMe));

  const vendorTeamUserIds = getVendorTeamUserIds(vendorTeamsResponse || []);

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (vendorTeamsQuery) return;
    dispatch(updateTeamsInterfaceQuery({ interfaceName, workingForMe }));
  }, [dispatch, vendorTeamsQuery, workingForMe]);

  useEffect(() => {
    if (vendorTeamsResponse || !vendorTeamsQuery || isLoadingVendorTeams || errorLoadingVendorTeams) return;
    dispatch(
      populateTeamsByQuery({
        session,
        workingForMe,
        interfaceName,
      }),
    );
  }, [
    dispatch,
    errorLoadingVendorTeams,
    isLoadingVendorTeams,
    session,
    vendorTeamsQuery,
    vendorTeamsResponse,
    workingForMe,
  ]);

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(vendorTeamUserIds.map((id) => id.value)) },
    { skip: vendorTeamUserIds.length === 0 },
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor((team) => <CompanyColumnCell activeTeam={team} />, {
        header: "Company",
        id: "company",
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor((team) => <LeaderColumnCell activeTeam={team} />, {
        header: "Leader",
        id: "leader",
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor((team) => <MembersColumnCell activeTeam={team} />, {
        header: "Members",
        id: "members",
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [],
  );

  const tableData = useMemo(() => vendorTeamsResponse ?? [], [vendorTeamsResponse]);

  const tableOptions: MRT_TableOptions<VendorTeam> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: () => ({
      onClick: () => {},
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <ToolbarButtonsContainer>
        <ToolbarButtons>
          <ToggleGlobalFilterButton table={table} />
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </ToolbarButtons>
      </ToolbarButtonsContainer>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Teams</TableTitle>
          <LowerTopToolbarContainer>
            <TeamsTabs activeTab={activePageTab} />
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row, index) =>
      `${row.leader?.userId?.value || "no-leader"}-${row.members?.map((m) => m.userId.value).join("-")}-${index}`,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: [] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: errorLoadingVendorTeams
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: vendorTeamsResponse?.length ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: !vendorTeamsResponse?.length && isLoadingVendorTeams,
      pagination,
      showAlertBanner: Boolean(errorLoadingVendorTeams),
      showProgressBars: isLoadingVendorTeams,
      sorting,
      showSkeletons: !vendorTeamsResponse?.length && isLoadingVendorTeams,
    },
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return <MRTNoMemo<VendorTeam> options={tableOptions} />;
}

function getVendorTeamUserIds(teams: VendorTeam[]): Guid[] {
  if (teams.length === 0) return [];
  return uniqBy(
    teams.flatMap((team) => [
      ...(team.leader?.userId ? [team.leader.userId] : []),
      ...(Array.isArray(team.members) ? team.members.map((m) => m.userId) : []),
    ]),
    (id) => id.value,
  );
}
